import CONFIG from "../config/config";
import Utils from "../utils/utils";
import Auth from "./auth";
import PubSub from "./PubSub";

const requestQueue = [];
let expiresAt;
let queueRequests = false;

const requestRemote = async ({ url, method, headers = {}, body }) => {
    if (!(body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
    }

    const requestObj = {
        method,
        mode: "cors",
        headers
    };

    if (body) {
        requestObj.body = JSON.stringify(body);
    }
    return fetchData({
        url,
        requestObj,
        errorHandler: (returnObject) => {
            PubSub.publish(
                "show_info",
                returnObject.data.error || "OTM_UNKNOWN_ERROR"
            );
        }
    })
        .then((response) => {
            return response;
        })
        .catch((e) => {
            PubSub.publish("show_info", e.message);
            return {
                error: true,
                data: {
                    error: e.message
                }
            };
        });
};

const request = async ({
    url,
    method,
    headers = {},
    body,
    showError,
    contentType = "application/json",
    showSpinner = true,
    handleUnauthorizedResponse = true
}) => {
    if (method !== "GET") {
        const deploymentId = window.location.pathname.split("/")?.[2];
        if (deploymentId) PubSub.publish("deployment-unlock-timer-refresh", { deploymentId })
    }

    url = CONFIG.API_PATH + url;
    if (!(body instanceof FormData)) {
        headers["Content-Type"] = contentType;
    }
    const authToken = window.sessionStorage.getItem("authToken");
    const domain = Utils.getDomain();
    if (authToken) {
        headers.Authorization = authToken;
    }
    if (domain && domain._id) {
        headers["x-app-domain"] = domain._id;
    }
    const requestObj = {
        method,
        mode: "cors",
        headers
    };
    if (body) {
        requestObj.body =
            body instanceof FormData ? body : JSON.stringify(body);
    }

    if (!queueRequests || headers.grant_type === "refresh_token") {
        const response = await fetchData({
            url,
            requestObj,
            showSpinner,
            handleUnauthorizedResponse
        });
        if (response.error && response.status !== 401 && showError) {
            PubSub.publish(
                "show_info",
                response.data.message ||
                response.data.error ||
                "OTM_UNKNOWN_ERROR",
                "OTM_ERROR",
                true,
                response.data.subCode || ""
            );

            if (response.data.subCode === 'err-290') {
                PubSub.publish("show-training-modal");
            }
        }
        return response;
    } else {
        PubSub.publish("show_spinner");
        let resolve;
        let reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        requestQueue.push({
            promise,
            resolve,
            reject,
            url,
            requestObj
        });
        return promise;
    }
};

const timer = setInterval(async () => {
    if (!expiresAt) {
        expiresAt =
            window.sessionStorage.getItem("expiresAt") &&
            Number(window.sessionStorage.getItem("expiresAt"));
    }
    queueRequests =
        expiresAt !== null && new Date().getTime() + 5000 > expiresAt;
    if (queueRequests) {
        await processRequestQueue();
    }
}, 2000);

window.addEventListener("unload", () => {
    clearInterval(timer);
});

const processRequestQueue = async () => {
    if (requestQueue.length > 0) {
        let newToken;
        expiresAt = null;
        const success = await Auth.getNewToken();
        if (success) {
            queueRequests = false;
            newToken = window.sessionStorage.getItem("authToken");

            while (requestQueue.length > 0) {
                const req = requestQueue.pop();
                req.requestObj.headers.Authorization = newToken;
                fetchData({
                    url: req.url,
                    requestObj: req.requestObj
                })
                    .then((response) => {
                        req.resolve(response);
                    })
                    .catch((e) => {
                        Utils.routeToLoginPage();
                    });
            }
        } else {
            Utils.routeToLoginPage();
        }
        PubSub.publish("hide_spinner");
    }
};

const fetchData = async ({
    url,
    requestObj,
    errorHandler,
    showSpinner = true,
    handleUnauthorizedResponse = true
}) => {
    if (showSpinner) {
        PubSub.publish("show_spinner");
    }
    return new Promise((resolve, reject) => {
        fetch(url, requestObj)
            .then(async (response) => {
                const returnObject = {
                    status: response.status,
                    url: response.url
                };

                if (response.status !== 204) {
                    const isBlob = response.headers
                        .get("Content-Type")
                        .includes("application/octet-stream");

                    const data = isBlob
                        ? await response.blob()
                        : await response.json();
                    returnObject.data = data;

                    if (isBlob) {
                        if (response.headers.get("Content-Disposition")) {
                            returnObject.fileName = response.headers
                                .get("Content-Disposition")
                                .split(";")
                                .find((name) => name.includes("filename="))
                                .replace("filename=", "")
                                .replaceAll('"', "")
                                .trim();
                        }
                    }
                }

                if ([200, 201, 204].indexOf(response.status) < 0) {
                    returnObject.error = true;
                    if (errorHandler) {
                        errorHandler(returnObject);
                    } else {
                        if (
                            response.status === 401 &&
                            handleUnauthorizedResponse
                        ) {
                            if (window.sessionStorage.getItem("refreshToken")) {
                                if (!queueRequests) {
                                    queueRequests = true;
                                    const success = await Auth.getNewToken();
                                    if (success) {
                                        window.location.reload();
                                    } else {
                                        Utils.routeToLoginPage();
                                    }
                                }
                            } else if (!Utils.isPrivatePage()) {
                                Utils.routeToLoginPage();
                            }
                        }
                    }
                }
                resolve(returnObject);
            })
            .catch((e) => {
                reject(e);
            })
            .finally(() => {
                if (showSpinner) {
                    PubSub.publish("hide_spinner");
                }
            });
    });
};

const get = async ({
    url,
    headers = {},
    showError = true,
    contentType,
    showSpinner,
    handleUnauthorizedResponse = true
}) => {
    return request({
        url,
        method: "GET",
        headers,
        showError,
        contentType,
        showSpinner,
        handleUnauthorizedResponse
    });
};

const del = async ({
    url,
    headers = {},
    showError = true,
    handleUnauthorizedResponse = true
}) => {
    return request({
        url,
        method: "DELETE",
        headers,
        showError,
        handleUnauthorizedResponse
    });
};

const post = async ({
    url,
    headers = {},
    body = {},
    showError = true,
    handleUnauthorizedResponse = true
}) => {
    return request({
        url,
        method: "POST",
        headers,
        body,
        showError,
        handleUnauthorizedResponse
    });
};

const put = async ({
    url,
    headers = {},
    body = {},
    showError = true,
    handleUnauthorizedResponse = true
}) => {
    return request({
        url,
        method: "PUT",
        headers,
        body,
        showError,
        handleUnauthorizedResponse
    });
};

const patch = async ({
    url,
    headers = {},
    body = {},
    showError = true,
    handleUnauthorizedResponse = true
}) => {
    return request({
        url,
        method: "PATCH",
        headers,
        body,
        showError,
        handleUnauthorizedResponse
    });
};

const RequestManager = {
    requestRemote,
    get,
    del,
    post,
    put,
    patch
};

export default RequestManager;
